import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Calendar, Shield, CheckCircle, Building, Star,
  Loader2, Settings, LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Layout from '../components/Layout';
import PropertyCard from '../components/PropertyCard';
import { usersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, updateProfile, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({ name: '', phone: '', role: '' });
  const [submitting, setSubmitting] = useState(false);

  const isOwnProfile = !userId || userId === user?.user_id;

  useEffect(() => {
    if (!authLoading) {
      if (isOwnProfile && !isAuthenticated) {
        navigate('/login');
        return;
      }
      fetchProfile();
    }
  }, [userId, user, isAuthenticated, authLoading]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      if (isOwnProfile && user) {
        setProfile({
          ...user,
          properties: [],
          review_count: 0
        });
        setEditData({
          name: user.name || '',
          phone: user.phone || '',
          role: user.role || 'renter'
        });
      } else if (userId) {
        const response = await usersApi.getProfile(userId);
        setProfile(response.data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Profile not found');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    setSubmitting(true);
    try {
      await updateProfile(editData);
      toast.success('Profile updated');
      setEditOpen(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="h-96 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }

  if (!profile) return null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <Avatar className="w-24 h-24">
              <AvatarImage src={profile.picture} />
              <AvatarFallback className="bg-trust-blue text-white text-2xl">
                {profile.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="font-heading text-2xl font-bold text-slate-900">
                    {profile.name}
                  </h1>
                  <Badge className="mt-2 capitalize">
                    {profile.role}
                  </Badge>
                </div>

                {isOwnProfile && (
                  <div className="flex gap-2">
                    <Dialog open={editOpen} onOpenChange={setEditOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid="edit-profile-btn">
                          <Settings className="w-4 h-4 mr-2" />
                          Edit Profile
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Edit Profile</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label htmlFor="edit-name">Name</Label>
                            <Input
                              id="edit-name"
                              value={editData.name}
                              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-phone">Phone</Label>
                            <Input
                              id="edit-phone"
                              value={editData.phone}
                              onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Account Type</Label>
                            <Select 
                              value={editData.role} 
                              onValueChange={(v) => setEditData({ ...editData, role: v })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="renter">Renter</SelectItem>
                                <SelectItem value="owner">Property Owner</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            onClick={handleUpdateProfile}
                            disabled={submitting}
                            className="w-full bg-trust-blue hover:bg-trust-blue-hover rounded-full"
                          >
                            {submitting ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleLogout}
                      className="text-red-600 hover:text-red-700"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </Button>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-2">
                {profile.email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span>{profile.email}</span>
                    {profile.email_verified && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                )}
                {profile.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" />
                    <span>{profile.phone}</span>
                    {profile.phone_verified && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>Member since {format(new Date(profile.created_at), 'MMMM yyyy')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h3 className="font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-trust-blue" />
              Verification Status
            </h3>
            <div className="flex flex-wrap gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                profile.email_verified ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
              }`}>
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Email {profile.email_verified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                profile.phone_verified ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
              }`}>
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Phone {profile.phone_verified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Properties (for owners) */}
        {profile.role === 'owner' && profile.properties?.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5" />
              {isOwnProfile ? 'My Properties' : 'Properties'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {profile.properties.map((property) => (
                <PropertyCard key={property.property_id} property={property} />
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          {profile.role === 'owner' && (
            <div className="bg-soft-gray rounded-xl p-4 text-center">
              <Building className="w-6 h-6 text-trust-blue mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-slate-900">
                {profile.properties?.length || 0}
              </p>
              <p className="text-sm text-slate-500">Listings</p>
            </div>
          )}
          <div className="bg-soft-gray rounded-xl p-4 text-center">
            <Star className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="font-heading text-2xl font-bold text-slate-900">
              {profile.review_count || 0}
            </p>
            <p className="text-sm text-slate-500">Reviews</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
